// Defends against dockview's own documented failure mode
// (https://github.com/mathuo/dockview/issues/341): calling `api.fromJSON()`
// with a layout that references a panel id `fromJSON` doesn't recognise
// leaves the internal grid mid-mutated and unrecoverable - even the
// documented recovery (`api.clear()`) then throws. The fix here is to never
// let `fromJSON` see that data in the first place: walk the persisted tree
// ourselves first and drop anything invalid, exactly as the roadmap calls
// for ("normalize-on-load ... drop unknown panel ids rather than
// throwing"). Workspace.tsx additionally never calls `api.clear()` as a
// recovery step for a `fromJSON` that throws anyway (see its `onReady`
// catch block) - this module is the first line of defence, that's the
// second.
import type { SerializedDockview } from 'dockview-react';

type SerializedGridNode = SerializedDockview['grid']['root'];
// The per-group state a leaf node's `data` carries (views/activeView/id) -
// dockview-core calls this `GroupPanelViewState` internally but doesn't
// re-export the name, so it's derived structurally instead.
type LeafGroupData = Exclude<SerializedGridNode['data'], SerializedGridNode[]>;

// `SerializedGridObject<T>` (dockview-core) is one flat interface - `data`
// is typed `T | SerializedGridObject<T>[]` regardless of `type` - not a
// true discriminated union TS can narrow from `node.type`. `Array.isArray`
// on `data` is the actual runtime discriminator; branch out on that instead
// of trying to derive a narrowed type from the `type` field.

/** Prunes one grid node to only reference ids in `validPanelIds`. Returns
 * `null` when the node (a leaf whose views all got dropped, or a branch
 * whose children all got dropped) has nothing valid left. */
function pruneNode(node: SerializedGridNode, validPanelIds: ReadonlySet<string>): SerializedGridNode | null {
  const data = node.data;
  if (Array.isArray(data)) {
    const children = data
      .map((child) => pruneNode(child, validPanelIds))
      .filter((child): child is SerializedGridNode => child !== null);
    if (children.length === 0) return null;
    return { ...node, data: children };
  }

  const groupData = data as LeafGroupData;
  const views = groupData.views.filter((id) => validPanelIds.has(id));
  if (views.length === 0) return null;
  const activeView = groupData.activeView && views.includes(groupData.activeView) ? groupData.activeView : views[0];
  // tabGroups (multi-row sub-tab-groups) aren't produced by this app - the
  // workspace never enables that module - so drop rather than re-validate.
  const { tabGroups: _tabGroups, ...rest } = groupData;
  return { ...node, data: { ...rest, views, activeView } };
}

function collectLeafGroupIds(node: SerializedGridNode, out: Set<string>): void {
  const data = node.data;
  if (Array.isArray(data)) {
    for (const child of data) collectLeafGroupIds(child, out);
    return;
  }
  out.add((data as LeafGroupData).id);
}

/**
 * Normalizes a persisted `SerializedDockview` against the panel ids valid
 * right now (registered components intersected with the project's current
 * 2D/3D projection - see `isDimensionValid`). Returns `null` when nothing
 * valid survives, meaning the caller should fall back to building a fresh
 * default layout instead of calling `fromJSON` at all.
 */
export function normalizeLayout(layout: SerializedDockview, validPanelIds: ReadonlySet<string>): SerializedDockview | null {
  const prunedRoot = pruneNode(layout.grid.root, validPanelIds);
  if (!prunedRoot) return null;

  const survivingGroupIds = new Set<string>();
  collectLeafGroupIds(prunedRoot, survivingGroupIds);

  const panels: SerializedDockview['panels'] = {};
  for (const [id, state] of Object.entries(layout.panels)) {
    if (validPanelIds.has(id)) panels[id] = state;
  }

  const activeGroup = layout.activeGroup && survivingGroupIds.has(layout.activeGroup) ? layout.activeGroup : undefined;

  // Floating/popout/edge groups are never produced by this workspace (no
  // free-form docking, per the roadmap's UX decision); drop them
  // defensively rather than try to validate a shape we never write.
  return {
    grid: { root: prunedRoot, height: layout.grid.height, width: layout.grid.width, orientation: layout.grid.orientation },
    panels,
    activeGroup,
  };
}
